/**
 * Chip — MD3 filter / assist / input chip for the Mango — Farmer Market kit.
 *
 * Per `preview/chips-badges.html`:
 *   - 8dp corner (cornerSmall), 32px tall, Noto Sans 14px.
 *   - Unselected: transparent with an outline.
 *   - Selected (filter) / assist colours use the *container* roles —
 *     `success` → secondaryContainer (leaf), `tertiary` → tertiaryContainer
 *     (mango), `alert` → errorContainer — with a leading icon and no border.
 *
 * Wraps MUI `Chip` but owns its prop surface (no `sx` leak). `leadingIcon`
 * takes a Material Symbols ligature name and renders through the kit `Icon`.
 */
import { Chip as MuiChip } from '@mui/material';
import { forwardRef, type MouseEventHandler } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, radius, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

/** Chip behaviour kind (MD3 chip assortment). */
export type ChipKind = 'filter' | 'assist' | 'input';

/**
 * Container colour for selected / assist chips. `warning` is an alias of
 * `tertiary` (mango) kept for backward-compat with legacy MUI-Chip callers
 * that passed `color="warning"`.
 */
export type ChipColor = 'neutral' | 'success' | 'tertiary' | 'alert' | 'warning';

export interface ChipProps {
  /** Visible label. */
  label: string;
  /** Chip kind. Default `assist`. */
  kind?: ChipKind;
  /** Container colour when selected / for assist chips. Default `success`. */
  color?: ChipColor;
  /** Selected state (filter chips). Drives the filled container look. */
  selected?: boolean;
  /** Leading Material Symbols ligature name, e.g. `check`, `verified`. */
  leadingIcon?: string;
  /** Disable interaction. */
  disabled?: boolean;
  /**
   * Density. Kept for backward-compat with legacy MUI-Chip callers; the kit
   * chip renders at a fixed 32px (rural-bumped) regardless. Accepted, ignored.
   */
  size?: 'small' | 'medium';
  /** Click / toggle handler. */
  onClick?: MouseEventHandler<HTMLDivElement>;
  /** Delete handler (input chips). Renders a trailing dismiss affordance. */
  onDelete?: () => void;
  /** Pass-through className for layout composition. */
  className?: string;
}

export const Chip = forwardRef<HTMLDivElement, ChipProps>(function Chip(
  {
    label,
    kind = 'assist',
    color = 'success',
    selected = false,
    leadingIcon,
    disabled,
    onClick,
    onDelete,
    className,
  },
  ref,
) {
  // Mode-aware: container roles tint correctly in both modes. In light they
  // resolve to the same brand values the static map used (secondaryContainer=
  // leaf[100], tertiaryContainer=mango[100], errorContainer=alert[50]).
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  const CONTAINER: Record<Exclude<ChipColor, 'neutral'>, { bg: string; fg: string }> = {
    success: { bg: sys.secondaryContainer, fg: sys.onSecondaryContainer },
    tertiary: { bg: sys.tertiaryContainer, fg: sys.onTertiaryContainer },
    warning: { bg: sys.tertiaryContainer, fg: sys.onTertiaryContainer },
    alert: { bg: sys.errorContainer, fg: sys.onErrorContainer },
  };

  // Filter chips show their filled container only while selected; assist chips
  // are always filled by their colour; input chips read as outlined neutral.
  const filled = kind === 'assist' || (kind === 'filter' && selected);
  const tones = color === 'neutral' ? null : CONTAINER[color];
  const useContainer = filled && tones !== null;

  return (
    <MuiChip
      ref={ref}
      label={label}
      disabled={disabled}
      onClick={onClick}
      onDelete={onDelete}
      className={className}
      icon={
        leadingIcon ? (
          <Icon name={leadingIcon} size={18} color="inherit" fill={useContainer} />
        ) : undefined
      }
      sx={{
        height: 32,
        borderRadius: `${radius.cornerSmall}px`,
        fontFamily: typography.fontFamily,
        fontSize: typography.labelLarge.fontSize,
        fontWeight: useContainer ? 600 : 500,
        px: useContainer ? 1 : 1.5,
        border: useContainer ? 'none' : `1px solid ${sys.outline}`,
        backgroundColor: useContainer ? tones.bg : 'transparent',
        color: useContainer ? tones.fg : sys.onSurfaceVariant,
        '& .MuiChip-icon': {
          color: 'inherit',
          marginLeft: 0,
          marginRight: '4px',
        },
        '& .MuiChip-deleteIcon': {
          color: 'inherit',
          opacity: 0.7,
          '&:hover': { opacity: 1, color: 'inherit' },
        },
        '&.Mui-disabled': { opacity: 0.4 }, // README: "Disabled: 40% opacity"
      }}
    />
  );
});
